import * as React from 'react'
import { cn } from '../../cn'
import { Button } from '@/components/ui/button'
import { Search, Bug, HelpCircle, Menu } from 'lucide-react'
import { SearchOverlay, type SearchResultItem } from '@/components/ui/SearchOverlay'
import { useServiceLayerAuth } from '@/lib/ServiceLayerAuthProvider'
import { useLanguage } from '../../providers/LanguageContext'
import { TopbarBell } from '@/components/ui/topbar.bell'
import { TopbarUserMenu } from '@/components/ui/topbar.user-menu'
// SubpageTabsHeader lives in the app layer (uses react-router); stub here for package build
const SubpageTabsHeader: React.FC<{ className?: string }> = () => null

export interface TopbarProps {
  className?: string
  onSearch?: (query: string) => void
  /** Fires on every keystroke in the search overlay (no debounce — consumer's job). */
  onSearchQueryChange?: (query: string) => void
  /** Inline result list rendered inside the search overlay. */
  searchResults?: SearchResultItem[]
  /** Fires when the user clicks an item in `searchResults`. */
  onSearchResultClick?: (item: SearchResultItem) => void
  searchPlaceholder?: string
  showSearch?: boolean
  showNotifications?: boolean
  showUserMenu?: boolean
  showHelp?: boolean
  showSidebarToggle?: boolean
  onSidebarToggle?: () => void
  notifications?: number
  user?: {
    name: string
    email: string
    avatar?: string
    role?: string
  }
  quickActions?: Array<{
    label: string
    icon: React.ReactNode
    onClick: () => void
  }>
  helpUrl?: string
  showAdminMenuItems?: boolean
  /** Bug-report trigger — fires the DebugReport modal. Sits left of the profile avatar. */
  onBugReport?: () => void
  /**
   * When set, the bell opens a popover with this content (e.g. inbox preview).
   * When omitted, the bell is non-interactive (visual badge only).
   */
  notificationsPanel?: React.ReactNode
  wacklerLens?: {
    active: boolean
    onToggle: () => void
  }
}

export function Topbar({
  className,
  onSearch,
  onSearchQueryChange,
  searchResults,
  onSearchResultClick,
  searchPlaceholder,
  showSearch = true,
  showNotifications = true,
  showUserMenu = true,
  showHelp = true,
  showSidebarToggle = true,
  onSidebarToggle,
  notifications = 0,
  user,
  quickActions = [],
  helpUrl = 'https://docs.clanda.de',
  showAdminMenuItems = true,
  onBugReport,
  notificationsPanel,
  wacklerLens,
}: TopbarProps) {
  const { t } = useLanguage()
  const { role: domainRole, signOut } = useServiceLayerAuth()
  const canImpersonate = domainRole === 'superadmin' || domainRole === 'sales_manager'
  const [isSearchOverlayOpen, setIsSearchOverlayOpen] = React.useState(false)

  const placeholderFull = searchPlaceholder || t('common.search.placeholder')
  const searchLabel = t('common.search')

  // Keyboard shortcut: Cmd/Ctrl+K opens search overlay
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        if (showSearch) setIsSearchOverlayOpen(true)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [showSearch])

  return (
    <>
    <header
      className={cn(
        // pt-safe: clears the iOS notch / Dynamic Island. Paired with
        // apple-mobile-web-app-status-bar-style=black-translucent in
        // index.html so the status bar overlays the topbar bg.
        // pl-safe / pr-safe: landscape-orientation safe-area gutters.
        'sticky top-0 z-10 w-full border-b header-glass glass-light flex flex-col max-h-fit pt-safe pl-safe pr-safe',
        className
      )}
    >
      {/* Main Topbar Row: left | center (search) | right */}
      <div className="flex h-14 items-center gap-2 sm:gap-3 layout-header-rail flex-shrink-0 min-w-0">
        {/* Left: sidebar toggle + CLANDA Portal branding */}
        <div className="shrink-0 flex items-center gap-2 min-w-0">
          {showSidebarToggle && (
            <Button
              variant="ghost"
              size="sm"
              className="h-10 w-10 p-0 rounded-full shrink-0 icon-circle-hover flex items-center justify-center"
              onClick={() => onSidebarToggle?.()}
              title="Toggle navigation"
              aria-label="Toggle navigation"
            >
              <Menu className="h-6 w-6" strokeWidth={2} />
            </Button>
          )}
          <span className="hidden sm:inline text-sm font-medium truncate max-w-label lg:max-w-dropdown text-foreground" title={t('header.portalName')}>
            {t('header.portalName')}
          </span>
        </div>

        {/* Center: search trigger (centered in header) */}
        {showSearch && (
          <div className="flex-1 min-w-0 flex justify-center px-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsSearchOverlayOpen(true)}
              className="w-full max-w-md h-10 justify-start gap-2 px-3 text-left border border-transparent hover:border-border rounded-md"
              aria-label={placeholderFull}
              title={`${placeholderFull} (⌘K)`}
            >
              <Search className="h-6 w-6 shrink-0 text-muted-foreground" strokeWidth={2} />
              <span className="hidden sm:inline truncate text-sm text-muted-foreground">{placeholderFull}</span>
            </Button>
          </div>
        )}

        {!showSearch && <div className="flex-1 min-w-0" />}

        {/* Quick Actions (desktop) */}
        {quickActions.length > 0 && (
          <div className="hidden xl:flex items-center gap-2 mr-2">
            {quickActions.slice(0, 3).map((action, index) => (
              <Button key={index} variant="ghost" size="sm" className="h-9 w-9 p-0" onClick={action.onClick} title={action.label}>
                {action.icon}
              </Button>
            ))}
          </div>
        )}

        {/* Right: Notifications, Help, 3-dots, Account */}
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {/* Notifications — see topbar.bell.tsx */}
          {showNotifications && (
            <TopbarBell
              notifications={notifications}
              notificationsPanel={notificationsPanel}
              title={t('header.notifications.title')}
            />
          )}

          {/* Help – in bar (Google-like); on small viewport may sit next to 3-dots */}
          {showHelp && (
            <Button
              variant="ghost"
              size="sm"
              className="h-10 w-10 p-0 shrink-0 rounded-full flex items-center justify-center"
              title={t('header.help')}
              aria-label={t('header.help')}
              onClick={() => window.open(helpUrl, '_blank', 'noopener,noreferrer')}
            >
              <HelpCircle className="h-6 w-6" strokeWidth={2} />
            </Button>
          )}

          {/* Bug report — opens DebugReport modal (full console + screenshot) */}
          {onBugReport && (
            <Button variant="ghost" size="sm" className="h-10 w-10 p-0 shrink-0 rounded-full flex items-center justify-center text-danger hover:bg-danger/10" title={t('header.bugReport')} aria-label={t('header.bugReport')} onClick={onBugReport}>
              <Bug className="h-6 w-6" strokeWidth={2} />
            </Button>
          )}

          {/* Unified account menu — extracted to keep topbar.tsx ≤300 LOC (House Rule #2). */}
          {showUserMenu && user && (
            <TopbarUserMenu
              user={user}
              showAdminMenuItems={showAdminMenuItems}
              canImpersonate={canImpersonate}
              signOut={signOut}
              t={t}
              wacklerLens={wacklerLens}
            />
          )}
        </div>
      </div>
      
      {/* GitHub-Style Subpage Tabs - Integrated into topbar */}
      <div className="flex-shrink-0 w-full overflow-hidden max-h-compact">
        <SubpageTabsHeader className="border-t-0" />
      </div>
    </header>
    {showSearch && (
      <SearchOverlay
        open={isSearchOverlayOpen}
        onClose={() => setIsSearchOverlayOpen(false)}
        onSearch={(q) => onSearch?.(q)}
        onQueryChange={onSearchQueryChange}
        results={searchResults}
        onResultClick={onSearchResultClick}
        placeholder={placeholderFull}
        searchLabel={searchLabel}
      />
    )}
  </>
  );
}
